.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* --- tones -------------------------------------------------------------- *
 *
 * Each tone sets three custom properties and nothing else. The components
 * below consume `--tone-fg`, `--tone-bg` and `--tone-border`, so adding a tone
 * is adding one block here and a name to the union in Status.tsx — not editing
 * every component that can carry one.
 */

.neutral {
  --tone-fg: var(--color-text-secondary);
  --tone-bg: var(--color-surface-sunken);
  --tone-border: var(--color-border-default);
  --tone-border-soft: var(--color-border-default);
}

.accent {
  --tone-fg: var(--color-accent-fg);
  --tone-bg: var(--color-accent-bg);
  --tone-border: var(--color-accent-emphasis);
  --tone-border-soft: color-mix(in oklab, var(--color-accent-emphasis) 28%, transparent);
}

.success {
  --tone-fg: var(--color-success-fg);
  --tone-bg: var(--color-success-bg);
  --tone-border: var(--color-success-emphasis);
  --tone-border-soft: color-mix(in oklab, var(--color-success-emphasis) 28%, transparent);
}

.warning {
  --tone-fg: var(--color-warning-fg);
  --tone-bg: var(--color-warning-bg);
  --tone-border: var(--color-warning-emphasis);
  --tone-border-soft: color-mix(in oklab, var(--color-warning-emphasis) 28%, transparent);
}

.danger {
  --tone-fg: var(--color-danger-fg);
  --tone-bg: var(--color-danger-bg);
  --tone-border: var(--color-danger-emphasis);
  --tone-border-soft: color-mix(in oklab, var(--color-danger-emphasis) 28%, transparent);
}

.info {
  --tone-fg: var(--color-info-fg);
  --tone-bg: var(--color-info-bg);
  --tone-border: var(--color-info-emphasis);
  --tone-border-soft: color-mix(in oklab, var(--color-info-emphasis) 28%, transparent);
}

.done {
  --tone-fg: var(--color-done-fg);
  --tone-bg: var(--color-done-bg);
  --tone-border: var(--color-done-emphasis);
  --tone-border-soft: color-mix(in oklab, var(--color-done-emphasis) 28%, transparent);
}

/* --- status indicator ---------------------------------------------------- */

.status {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--tone-fg);
  font-size: var(--text-base);
  line-height: var(--text-base-line);
  white-space: nowrap;
}

.glyph {
  flex: none;
}

.statusLabel {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --- badge ---------------------------------------------------------------- */

.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 24px;
  padding: 0 var(--space-3);
  border: var(--border-width-thin) solid transparent;
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.02em;
  text-transform: uppercase;
  line-height: 1;
  white-space: nowrap;
  color: var(--tone-fg);
}

.subtle {
  background: var(--tone-bg);
  /* The tint alone is faint on white; a hairline in the same family gives the
   * pill an edge without turning it into an outline badge. */
  box-shadow: inset 0 0 0 var(--border-width-thin) var(--tone-border-soft, transparent);
}

.outline {
  border-color: var(--tone-border);
}

/* --- count ---------------------------------------------------------------- */

.count {
  display: inline-flex;
  align-items: baseline;
  gap: var(--space-1);
  /* Lining tabular figures: these sit in columns, and proportional digits make
   * a column of numbers look misaligned even when it is not. */
  font-variant-numeric: tabular-nums lining-nums;
  font-feature-settings: "tnum" 1;
  font-weight: var(--font-weight-medium);
  color: var(--tone-fg);
}

/* The marker is quieter than the number: it identifies the column, the number
 * is the reading. Same colour family, so the pair still reads as one unit. */
.countMarker {
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.04em;
  opacity: var(--opacity-muted);
}
